"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import jsPDF from "jspdf";
import logo from "../assets/DesarrollandoTerritorios.png"
import { apiConfig } from "../config/api.config";
import autoTable from "jspdf-autotable";

// =======================
// Utilidades
// =======================
const numeroATexto = (numero) => {
  const unidades = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
  const decenas = ["", "", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
  const especiales = ["DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISÉIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE"];
  const centenas = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

  const n = Number(numero || 0);
  if (n === 0) return "CERO";
  if (n === 100) return "CIEN";

  let texto = "";
  let num = Math.floor(Math.abs(n));

  if (num >= 1000000) {
    const millones = Math.floor(num / 1000000);
    texto += millones === 1 ? "UN MILLÓN " : numeroATexto(millones) + " MILLONES ";
    num %= 1000000;
  }

  if (num >= 1000) {
    const miles = Math.floor(num / 1000);
    texto += miles === 1 ? "MIL " : numeroATexto(miles) + " MIL ";
    num %= 1000;
  }

  if (num >= 100) {
    const cent = Math.floor(num / 100);
    texto += centenas[cent] + " ";
    num %= 100;
  }

  if (num >= 20) {
    const dec = Math.floor(num / 10);
    texto += decenas[dec];
    num %= 10;
    if (num > 0) texto += " Y " + unidades[num];
  } else if (num >= 10) {
    texto += especiales[num - 10];
  } else if (num > 0) {
    texto += unidades[num];
  }

  return texto.trim();
};

const toInt = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return Math.floor(v);
  const s = String(v).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[^\d-]/g, "");
  const n = parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : 0;
};

const splitSmart = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.map((x) => String(x).trim()).filter(Boolean);

  const s = String(value).trim();
  if (!s) return [];

  if (s.includes("||")) return s.split("||").map((x) => String(x).trim()).filter(Boolean);
  if (s.includes(",")) return s.split(",").map((x) => String(x).trim()).filter(Boolean);

  return [s];
};

// Token defensivo (no rompe si no existe)
const getTokenLoose = () => {
  try {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("access_token") ||
      ""
    );
  } catch {
    return "";
  }
};

// =======================
// Normalización de contrato
// =======================
const normalizeContract = (c) => {
  if (!c || typeof c !== "object") return null;

  return {
    id: c.id,

    radicado: c.radicado ?? "",
    secretaria: c.secretaria ?? "",

    naturalezaGasto: c.naturalezaGasto ?? c.naturaleza_gasto ?? "",
    presupuestoTotal: c.presupuestoTotal ?? c.presupuesto_total ?? 0,

    objetoContractual: c.objetoContractual ?? c.objeto_contractual ?? "",
    centroCostos: c.centroCostos ?? c.centro_costos ?? "",
    nombreSolicitante: c.nombreSolicitante ?? c.nombre_solicitante ?? "",
    cargoSolicitante: c.cargoSolicitante ?? c.cargo_solicitante ?? "",

    firma_solicitante_url: c.firma_solicitante_url ?? c.firmaSolicitante_url ?? c.firmaUrl ?? "",
    firma_alcalde_url: c.firma_alcalde_url ?? c.firmaAlcalde_url ?? "",

    discriminacion_rubros: c.discriminacion_rubros ?? c.discriminacionRubros ?? "",
    rubro: c.rubro ?? c.rubros ?? "",
    valor: c.valor ?? c.valores ?? "",

    rubrosData: c.rubrosData ?? c.rubros_data ?? null,
  };
};

// =======================
// Parser robusto de rubros
// =======================
const buildRubrosFromContract = (nc) => {
  if (!nc) return [];

  // 1) Array directo
  if (Array.isArray(nc.rubrosData)) {
    return nc.rubrosData
      .map((r) => ({
        resumido: String(r?.resumido ?? r?.codigo ?? r?.rubro ?? "").trim(),
        componenteInversion: String(r?.componenteInversion ?? r?.componente_inversion ?? r?.componente ?? "").trim(),
        valor: toInt(r?.valor),
      }))
      .filter((r) => r.resumido || r.valor);
  }

  // 2) JSON string
  if (typeof nc.rubrosData === "string") {
    try {
      const parsed = JSON.parse(nc.rubrosData);
      if (Array.isArray(parsed)) {
        return parsed
          .map((r) => ({
            resumido: String(r?.resumido ?? r?.codigo ?? r?.rubro ?? "").trim(),
            componenteInversion: String(r?.componenteInversion ?? r?.componente_inversion ?? r?.componente ?? "").trim(),
            valor: toInt(r?.valor),
          }))
          .filter((r) => r.resumido || r.valor);
      }
    } catch {
      // cae a legacy
    }
  }

  // 3) Legacy por campos separados
  const codigos = splitSmart(nc.discriminacion_rubros).length
    ? splitSmart(nc.discriminacion_rubros)
    : splitSmart(nc.rubro);

  const valoresRaw = splitSmart(nc.valor);

  if (!codigos.length) return [];

  return codigos
    .map((codigo, idx) => {
      const valorCrudo = valoresRaw[idx] ?? (valoresRaw.length === 1 ? valoresRaw[0] : "0");
      return {
        resumido: String(codigo || "").trim(),
        componenteInversion: "",
        valor: toInt(valorCrudo),
      };
    })
    .filter((r) => r.resumido);
};

// =======================
// Helpers PDF
// =======================
const toDataURL = async (url) => {
  // Convierte una URL (misma-origin o con CORS) a dataURL para addImage
  // Si falla, retorna null para no romper el PDF.
  try {
    const resp = await fetch(url, { mode: "cors" });
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

function CDPGenerator({ contratoFirmado, onClose, user }) {
  const normalizedContract = useMemo(() => normalizeContract(contratoFirmado), [contratoFirmado]);

  const printRef = useRef(null);

  const [formData, setFormData] = useState({
    fecha: "",
    dependencia: "",
    vigenciaFutura: "NO",
    documentoVigenciaFutura: null,
    tipoGasto: "",
    codigoBancop: "",
    objetoIndicador: "",
    centroCostos: "",
    valorNumerico: "",
    valorLetras: "",
    solicitante: "",
    cargoSolicitante: "",
    firmaUrl: "",
    firmaAlcaldeUrl: "",
  });

  const [rubrosData, setRubrosData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!normalizedContract) return;

    const obtenerFechaLocal = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const presupuestoNumerico = toInt(normalizedContract.presupuestoTotal);
    const presupuestoEnTexto = numeroATexto(presupuestoNumerico);
    const rubros = buildRubrosFromContract(normalizedContract);

    setFormData((prev) => ({
      ...prev,
      fecha: obtenerFechaLocal(),
      dependencia: normalizedContract.secretaria || "",
      vigenciaFutura: "NO",
      documentoVigenciaFutura: null,
      tipoGasto: normalizedContract.naturalezaGasto || "",
      codigoBancop: "",
      objetoIndicador: normalizedContract.objetoContractual || "",
      centroCostos: normalizedContract.centroCostos || "",
      valorNumerico: presupuestoNumerico,
      valorLetras: presupuestoEnTexto,
      solicitante: normalizedContract.nombreSolicitante || "",
      cargoSolicitante: normalizedContract.cargoSolicitante || "",
      firmaUrl: normalizedContract.firma_solicitante_url || "",
      firmaAlcaldeUrl: normalizedContract.firma_alcalde_url || "",
    }));

    setRubrosData(rubros);
  }, [normalizedContract]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRubroChange = (index, field, value) => {
    setRubrosData((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // =======================
  // Guardar CDP en backend
  // =======================
  const guardarCDPEnBackend = async () => {
    const token = getTokenLoose();

    const resolvedContractId = contratoFirmado?.id || normalizedContract?.id || null;
    if (!resolvedContractId) {
      throw new Error("No se pudo resolver contract_id para guardar el CDP.");
    }

    const rows = Array.isArray(rubrosData) ? rubrosData : [];

    const payload = {
      contract_id: resolvedContractId,

      fecha: formData?.fecha || null,
      dependencia: formData?.dependencia || "",
      vigenciaFutura: formData?.vigenciaFutura || "NO",
      documentoVigenciaFutura: formData?.documentoVigenciaFutura || null,
      tipoGasto: formData?.tipoGasto || "",
      codigoBancop: formData?.codigoBancop || null,
      objetoIndicador: formData?.objetoIndicador || "",
      centroCostos: formData?.centroCostos || "",
      valorNumerico: formData?.valorNumerico != null ? String(formData.valorNumerico) : "",
      valorLetras: formData?.valorLetras || "",
      solicitante: formData?.solicitante || "",
      cargoSolicitante: formData?.cargoSolicitante || "",

      firmaUrl: formData?.firmaUrl || null,
      firmaAlcaldeUrl: formData?.firmaAlcaldeUrl || null,

      rubrosData: JSON.stringify(rows),
    };

    const url = apiConfig?.routes?.cdpCreate ? apiConfig.routes.cdpCreate() : "/api/cdp/create/index.php";

    console.log("Guardando CDP en:", url, payload);

    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    const raw = await resp.text().catch(() => "");

    if (!resp.ok) {
      throw new Error(raw || `Error HTTP ${resp.status}`);
    }

    try {
      return raw ? JSON.parse(raw) : { ok: true };
    } catch {
      return { ok: true, raw };
    }
  };

  // =======================
  // PDF INSTITUCIONAL (DIBUJADO)
  // =======================
  const generarPDF = async () => {
    if (!normalizedContract) throw new Error("No hay contrato normalizado.");

    const doc = new jsPDF("p", "mm", "a4");

    // Márgenes y helpers
    const left = 10;
    const right = 200; // A4 width 210 - margen 10
    const maxWidth = right - left;

    // -------- Logo (asset local) --------
    // Si tu logo es PNG local (import), esto suele funcionar directo.
    // Si llega a fallar en prod por type, igual no rompe; solo omite.
    try {
      doc.addImage(logo, "PNG", left, 10, 25, 25);
    } catch {
      // omitimos logo si falla
    }

    // -------- Encabezado --------
    doc.setFont("Times", "bold");
doc.setFontSize(11);
doc.text("ALCALDÍA DE COPACABANA", 105, 12, { align: "center" });

doc.setFontSize(12);                 // 👈 antes estaba más grande
doc.text("SOLICITUD DE DISPONIBILIDAD PRESUPUESTAL", 105, 19, { align: "center" });

doc.setFont("Times", "normal");      // 👈 lo hago normal para que “no pese” tanto
doc.setFontSize(9);
doc.text("Código: A-CBS-F-013", 150, 12);
doc.text("Versión: 10", 150, 16);
doc.text("Fecha Actualización: 13-01-2025", 150, 20);

// Línea separadora
doc.setLineWidth(0.2);
doc.line(left, 36, right, 36);

    // -------- Cuerpo --------
    doc.setFont("Times", "normal");
    doc.setFontSize(10);

    let y = 45;

    doc.text(`FECHA: ${formData.fecha || ""}`, left, y);
    y += 7;

    doc.text(`DEPENDENCIA: ${formData.dependencia || ""}`, left, y);
    y += 7;

    // Vigencia futura
    const vf = (formData.vigenciaFutura || "NO").toUpperCase();
    doc.text("VIGENCIA FUTURA: SI _____  NO _____", left, y);
    // Marca con X
    if (vf === "SI") doc.text("X", left + 44, y);
    else doc.text("X", left + 66, y);
    y += 7;

    doc.text(`DOCUMENTO VIGENCIA FUTURA: ${formData.documentoVigenciaFutura || "N/A"}`, left, y);
    y += 7;

    // Tipo de gasto
    const tg = (formData.tipoGasto || "").toLowerCase();
    doc.text("TIPO DE GASTO: INVERSIÓN _____  FUNCIONAMIENTO _____  DEUDA PÚBLICA _____", left, y);
    if (tg.includes("invers")) doc.text("X", left + 47, y);
    else if (tg.includes("funcion")) doc.text("X", left + 85, y);
    else if (tg.includes("deuda")) doc.text("X", left + 136, y);
    y += 7;

    doc.text(`CÓDIGO DEL BANCOP: ${formData.codigoBancop || ""}`, left, y);
    y += 7;

    // Objeto
    doc.setFont("Times", "bold");
    doc.text("OBJETO CON INDICADOR DEL PLAN DE DESARROLLO", left, y);
    y += 6;

    doc.setFont("Times", "normal");
    const objetoLines = doc.splitTextToSize(formData.objetoIndicador || "", maxWidth);
    doc.text(objetoLines, left, y);
    y += objetoLines.length * 5 + 2;

    // -------- Rubros --------
    doc.setFont("Times", "bold");
    doc.text("RUBRO PRESUPUESTAL:", left, y);
    y += 4;

    // Tabla con autoTable (USO CORRECTO)
    const body = (Array.isArray(rubrosData) ? rubrosData : []).map((r) => [
      String(r?.resumido || ""),
      String(r?.componenteInversion || ""),
      Number(r?.valor || 0).toLocaleString("es-CO"),
    ]);

   autoTable(doc, {
  startY: y,
  head: [["RESUMIDO", "COMPONENTE INV.", "VALOR"]],
  body,
  theme: "grid",
  styles: {
    fontSize: 9,
    cellPadding: 2,
    lineWidth: 0.1,
    lineColor: [30, 58, 138], // 👈 azul (bordes)
  },
  headStyles: {
    fillColor: [30, 58, 138], // 👈 azul encabezado
    textColor: 255,           // 👈 blanco
    fontStyle: "bold",
    halign: "center",
    lineWidth: 0.1,
    lineColor: [30, 58, 138],
  },
  columnStyles: {
    0: { cellWidth: 35 },
    1: { cellWidth: 95 },
    2: { halign: "right", cellWidth: 35 },
  },
  margin: { left, right: 10 },
});
    const afterTableY = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 6 : y + 20;

    // -------- Totales y firmas --------
    doc.setFont("Times", "normal");
    let y2 = afterTableY;

    doc.text(`CENTRO DE COSTOS: ${formData.centroCostos || ""}`, left, y2);
    y2 += 7;

    const valorLetras = formData.valorLetras || "";
    const valorLetrasLines = doc.splitTextToSize(`VALOR: ${valorLetras}`, maxWidth);
    doc.text(valorLetrasLines, left, y2);
    y2 += valorLetrasLines.length * 5 + 2;

    doc.text(`ML $ ${Number(formData.valorNumerico || 0).toLocaleString("es-CO")}`, left, y2);
    y2 += 8;

    doc.text(`SOLICITANTE: ${formData.solicitante || ""}`, left, y2);
    y2 += 7;

    doc.text(`CARGO SOLICITANTE: ${formData.cargoSolicitante || ""}`, left, y2);
    y2 += 10;

    // Firma solicitante
    if (formData.firmaUrl) {
      // Si es URL, intentamos convertir a dataURL
      const isUrl = typeof formData.firmaUrl === "string" && /^https?:\/\//i.test(formData.firmaUrl);
      const firmaDataUrl = isUrl ? await toDataURL(formData.firmaUrl) : formData.firmaUrl;

      if (firmaDataUrl) {
        doc.setFont("Times", "bold");
        doc.text("FIRMA SOLICITANTE:", left, y2);
        doc.setFont("Times", "normal");
        try {
          doc.addImage(firmaDataUrl, "PNG", left + 45, y2 - 5, 50, 18);
        } catch {
          // si el formato no es PNG, igual no rompemos
        }
        y2 += 18;
      }
    }

    // Firma alcalde (si la quieres en el mismo CDP)
    if (formData.firmaAlcaldeUrl) {
      const isUrl = typeof formData.firmaAlcaldeUrl === "string" && /^https?:\/\//i.test(formData.firmaAlcaldeUrl);
      const firmaDataUrl = isUrl ? await toDataURL(formData.firmaAlcaldeUrl) : formData.firmaAlcaldeUrl;

      if (firmaDataUrl) {
        doc.setFont("Times", "bold");
        doc.text("FIRMA ALCALDE:", left, y2);
        doc.setFont("Times", "normal");
        try {
          doc.addImage(firmaDataUrl, "PNG", left + 45, y2 - 5, 50, 18);
        } catch {}
        y2 += 18;
      }
    }

    // Footer
    doc.setFont("Times", "normal");
    doc.setFontSize(9);
    doc.text("Página 1 de 1 • Oficina Jurídica", 105, 290, { align: "center" });

    const rad = normalizedContract?.radicado || "sin_radicado";
    doc.save(`CDP_${rad}.pdf`);
  };

  // =======================
  // Generar + Guardar
  // =======================
  const handleGenerarCDP = async () => {
    setLoading(true);

    try {
      const camposRequeridos = [
        "fecha",
        "dependencia",
        "tipoGasto",
        "objetoIndicador",
        "centroCostos",
        "valorNumerico",
        "valorLetras",
        "solicitante",
        "cargoSolicitante",
      ];

      const faltantes = camposRequeridos.filter((c) => !formData?.[c]);
      if (faltantes.length) {
        alert(`Por favor complete: ${faltantes.join(", ")}`);
        return;
      }

      const rows = Array.isArray(rubrosData) ? rubrosData : [];
      if (!rows.length) {
        alert("No hay rubros disponibles para este contrato.");
        return;
      }

      const incompletos = rows.some((r) => !r?.componenteInversion || String(r.componenteInversion).trim() === "");
      if (incompletos) {
        alert("Completa el 'Componente de Inversión' en todos los rubros.");
        return;
      }

      const saved = await guardarCDPEnBackend();
      console.log("✅ CDP guardado en backend:", saved);

      await generarPDF();

      alert("CDP generado, guardado y descargado correctamente.");
    } catch (error) {
      console.error("Error al generar CDP:", error);
      alert("Error al generar el CDP: " + (error.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

  if (!normalizedContract) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        padding: "1rem",
        boxSizing: "border-box",
      }}
    >
      <div
        className="container"
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div ref={printRef} style={{ background: "#fff" }}>
          <div className="form-header">
            <div
              style={{
                padding: "5px",
                borderRadius: "4px",
                display: "inline-block",
                backgroundColor: "transparent",
              }}
            >
              <img src={logo || "/placeholder.svg"} alt="Logo" className="logo" />
            </div>

            <div>
              <h3>SOLICITUD DE</h3>
              <h3>DISPONIBILIDAD PRESUPUESTAL</h3>
            </div>

            <div className="form-id">
              <span style={{ display: "block" }}>Código: A-CBS-F-013</span>
              <span style={{ display: "block" }}>Versión: 10</span>
              <span style={{ display: "block" }}>Fecha Actualización: 13-01-2025</span>
            </div>
          </div>

          <div className="form-content">
            <table className="form-table">
              <tbody>
                <tr>
                  <th style={{ width: "200px" }}>
                    FECHA: <span className="required">*</span>
                  </th>
                  <td>
                    <input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange("fecha", e.target.value)}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    DEPENDENCIA: <span className="required">*</span>
                  </th>
                  <td>
                    <input
                      type="text"
                      value={formData.dependencia}
                      onChange={(e) => handleInputChange("dependencia", e.target.value)}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>VIGENCIA FUTURA:</th>
                  <td>
                    <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                          type="radio"
                          name="vigenciaFutura"
                          value="SI"
                          checked={formData.vigenciaFutura === "SI"}
                          onChange={(e) => handleInputChange("vigenciaFutura", e.target.value)}
                          style={{ accentColor: "#22c55e" }}
                        />
                        SI
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                          type="radio"
                          name="vigenciaFutura"
                          value="NO"
                          checked={formData.vigenciaFutura === "NO"}
                          onChange={(e) => handleInputChange("vigenciaFutura", e.target.value)}
                        />
                        NO
                      </label>
                    </div>
                  </td>
                </tr>

                <tr>
                  <th>DOCUMENTO VIGENCIA FUTURA:</th>
                  <td>
                    <input
                      type="text"
                      value={formData.documentoVigenciaFutura || ""}
                      onChange={(e) => handleInputChange("documentoVigenciaFutura", e.target.value)}
                      className="form-control"
                      placeholder="Ingrese el nombre o referencia del documento"
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    TIPO DE GASTO: <span className="required">*</span>
                  </th>
                  <td>
                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                          type="radio"
                          name="tipoGasto"
                          value="Inversión"
                          checked={formData.tipoGasto === "Inversión"}
                          onChange={(e) => handleInputChange("tipoGasto", e.target.value)}
                          style={{ accentColor: "#22c55e" }}
                        />
                        INVERSIÓN:
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                          type="radio"
                          name="tipoGasto"
                          value="Funcionamiento"
                          checked={formData.tipoGasto === "Funcionamiento"}
                          onChange={(e) => handleInputChange("tipoGasto", e.target.value)}
                          style={{ accentColor: "#22c55e" }}
                        />
                        FUNCIONAMIENTO:
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                          type="radio"
                          name="tipoGasto"
                          value="Servicio de la Deuda"
                          checked={formData.tipoGasto === "Servicio de la Deuda"}
                          onChange={(e) => handleInputChange("tipoGasto", e.target.value)}
                          style={{ accentColor: "#22c55e" }}
                        />
                        DEUDA PÚBLICA:
                      </label>
                    </div>
                  </td>
                </tr>

                <tr>
                  <th>CÓDIGO DEL BANCOP:</th>
                  <td>
                    <input
                      type="text"
                      value={formData.codigoBancop}
                      onChange={(e) => handleInputChange("codigoBancop", e.target.value)}
                      className="form-control"
                      placeholder="Ingrese código(s) del BANCOP separados por coma si son varios"
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    OBJETO CON INDICADOR DEL PLAN DE DESARROLLO: <span className="required">*</span>
                  </th>
                  <td>
                    <textarea
                      value={formData.objetoIndicador}
                      onChange={(e) => handleInputChange("objetoIndicador", e.target.value)}
                      rows={4}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    RUBRO PRESUPUESTAL: <span className="required">*</span>
                  </th>
                  <td>
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          border: "1px solid #ccc",
                          borderCollapse: "collapse",
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: "#f5f5f5" }}>
                            <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", minWidth: "120px" }}>
                              RESUMIDO
                            </th>
                            <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", minWidth: "180px" }}>
                              COMPONENTE DE INVERSIÓN
                            </th>
                            <th style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", minWidth: "100px" }}>
                              VALOR
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {rubrosData.length > 0 ? (
                            rubrosData.map((rubro, index) => (
                              <tr key={index}>
                                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                                  <input
                                    type="text"
                                    value={rubro.resumido || ""}
                                    readOnly
                                    style={{
                                      width: "100%",
                                      border: "none",
                                      background: "transparent",
                                      fontSize: "12px",
                                    }}
                                  />
                                </td>

                                <td style={{ border: "1px solid #ccc", padding: "4px" }}>
                                  <input
                                    type="text"
                                    value={rubro.componenteInversion || ""}
                                    onChange={(e) => handleRubroChange(index, "componenteInversion", e.target.value)}
                                    placeholder="Ingrese componente de inversión"
                                    style={{
                                      width: "100%",
                                      padding: "4px",
                                      fontSize: "11px",
                                      border: "1px solid #ddd",
                                      borderRadius: "4px",
                                    }}
                                    required
                                  />
                                </td>

                                <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "right" }}>
                                  <input
                                    type="text"
                                    value={
                                      rubro.valor !== undefined && rubro.valor !== null && rubro.valor !== ""
                                        ? Number(rubro.valor).toLocaleString("es-CO")
                                        : ""
                                    }
                                    readOnly
                                    style={{
                                      width: "100%",
                                      border: "none",
                                      background: "transparent",
                                      textAlign: "right",
                                      fontSize: "12px",
                                    }}
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" style={{ border: "1px solid #ccc", padding: "20px", textAlign: "center", color: "#666" }}>
                                No hay rubros disponibles para este contrato
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>

                <tr>
                  <th>
                    CENTRO DE COSTOS: <span className="required">*</span>
                  </th>
                  <td>
                    <input
                      type="text"
                      value={formData.centroCostos}
                      onChange={(e) => handleInputChange("centroCostos", e.target.value)}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    VALOR: (En letras) <span className="required">*</span>
                  </th>
                  <td>
                    <textarea
                      value={formData.valorLetras}
                      onChange={(e) => handleInputChange("valorLetras", e.target.value)}
                      rows={2}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    VALOR: (Numérico) <span className="required">*</span>
                  </th>
                  <td>
                    <input
                      type="text"
                      value={formData.valorNumerico ? Number(formData.valorNumerico).toLocaleString("es-CO") : ""}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[.,]/g, "");
                        handleInputChange("valorNumerico", numericValue);
                      }}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    SOLICITANTE: <span className="required">*</span>
                  </th>
                  <td>
                    <input
                      type="text"
                      value={formData.solicitante}
                      onChange={(e) => handleInputChange("solicitante", e.target.value)}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                <tr>
                  <th>
                    CARGO SOLICITANTE: <span className="required">*</span>
                  </th>
                  <td>
                    <input
                      type="text"
                      value={formData.cargoSolicitante}
                      onChange={(e) => handleInputChange("cargoSolicitante", e.target.value)}
                      className="form-control"
                      required
                    />
                  </td>
                </tr>

                {formData.firmaUrl && (
                  <tr>
                    <th>FIRMA SOLICITANTE:</th>
                    <td>
                      <div className="firma-preview">
                        <img src={formData.firmaUrl || "/placeholder.svg"} alt="Firma del solicitante" className="firma-image" />
                      </div>
                    </td>
                  </tr>
                )}

                {formData.firmaAlcaldeUrl && (
                  <tr>
                    <th>FIRMA Alcalde:</th>
                    <td>
                      <div className="firma-preview">
                        <img src={formData.firmaAlcaldeUrl || "/placeholder.svg"} alt="Firma del alcalde" className="firma-image" />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="form-footer">
            <p>Página 1 de 1 • Oficina Jurídica</p>
          </div>
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="btn btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button onClick={handleGenerarCDP} className="btn btn-primary" disabled={loading}>
            {loading ? "Generando..." : "Generar y Guardar CDP"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CDPGenerator;
